import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { AuthContext } from '../contexts/auth';

import Fetching from "./fetching";
import OuSelectionPair from './modal_select_OU_to_pair';
import CalendarMonthSelect from './calendar_month_select';
import SchedulerContextBar from './scheduler_context_bar';

import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Collapse, IconButton, Box, Typography
} from '@mui/material';

import {
    getJustificationRequestsCompanyList,
    updateJustificationRequestStatus,
    deleteJustificationRequest,
    getJustificationRequestsSummary,
    bulkUpdateJustificationRequestsStatus
} from '../services/api_justification_requests';

import { listOrganizationalUnits } from '../services/api_org_unit';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import SaveOutlinedIcon      from '@mui/icons-material/SaveOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';

import CustomTooltipComponent from './custom_tooltip_component';
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

// --- util ---
function formatJustificationEnd(datetimeFrom, datetimeTo) {
    const from = moment(datetimeFrom);
    const to = moment(datetimeTo);
    const isNextDayMidnight =
        to.isSame(from.clone().add(1, 'days').startOf('day'), 'day') && to.format('HH:mm') === '00:00';
    return {
        date: isNextDayMidnight ? from.format('DD/MM/YYYY') : to.format('DD/MM/YYYY'),
        time: isNextDayMidnight ? '24:00' : to.format('HH:mm'),
    };
}

const EMP_PAGE_SIZE = 25;
const HEADER_OFFSET = 80;

const GiustificativiRichiesteNull = () => {
    const { token } = useContext(AuthContext);

    // --- SUMMARY (righe principali) ---
    const [groups, setGroups] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);

    // --- Dettagli per dipendente ---
    const [employeeItems, setEmployeeItems] = useState({});

    // --- Filtri ---
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [fromDate, setFromDate] = useState(moment(selectedMonth).startOf('month').format('YYYY-MM-DD'));
    const [toDate, setToDate]     = useState(moment(selectedMonth).endOf('month').format('YYYY-MM-DD'));
    const employeesKey = useMemo(
        () => selectedEmployees.map(e => e.id).sort((a,b) => a-b).join(','),
        [selectedEmployees]
    );

    // --- UO gestite qui e mostrate dalla SchedulerContextBar ---
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [isUnitsLoading, setIsUnitsLoading] = useState(false);

    // --- UI ---
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError]     = useState(false);
    const [openGroups, setOpenGroups] = useState(() => new Set());
    const hasGroups = !isLoading && groups.length > 0;

    // --- Modal OU & note ---
    const [showUnitSelection, setShowUnitSelection] = useState(false);
    const [selectedJustification, setSelectedJustification] = useState(null);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState('');

    const [totalPendingAll, setTotalPendingAll] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);

    // --- Abort controllers ---
    const summaryCtrlRef = useRef(null);
    const detailCtrlsRef = useRef(new Map());

    // Anchor per scroll all'inizio lista del dipendente
    const anchorRefs = useRef(new Map());
    const setEmpAnchorRef = (empId) => (el) => {
        if (el) anchorRefs.current.set(empId, el);
        else anchorRefs.current.delete(empId);
    };
    const scrollToEmpTop = (empId, offset = HEADER_OFFSET) => {
        const el = anchorRefs.current.get(empId);
        if (!el) return;
            const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ top: y, behavior: 'smooth' });
    };

    // Abort helpers
    const abortDetail = (empId) => {
        const ctrl = detailCtrlsRef.current.get(empId);
        if (ctrl) {
        try { ctrl.abort(); } catch {}
        detailCtrlsRef.current.delete(empId);
        }
    };
    const abortAllDetails = () => {
        for (const ctrl of detailCtrlsRef.current.values()) {
        try { ctrl.abort(); } catch {}
        }
        detailCtrlsRef.current.clear();
    };

    // Caricamento UO per la SchedulerContextBar
    useEffect(() => {
        const fetchUnits = async () => {
        setIsUnitsLoading(true);
        setIsError(false);
        try {
            const response = await listOrganizationalUnits(token, true, null, 'justifications');
            setUnits(response.data || []);
        } catch (error) {
            console.error('Error fetching units:', error);
            if (error?.response?.status !== 402) setIsError(true);
        } finally {
            setIsUnitsLoading(false);
        }
        };
        fetchUnits();
    }, [token]);

    // Deriva date dal mese selezionato
    useEffect(() => {
        setFromDate(moment(selectedMonth).startOf('month').format('YYYY-MM-DD'));
        setToDate(moment(selectedMonth).endOf('month').format('YYYY-MM-DD'));
    }, [selectedMonth]);

    // Reset forte quando cambia dataset (mese/OU/dipendenti/pageSize)
    useEffect(() => {
        if (summaryCtrlRef.current) { try { summaryCtrlRef.current.abort(); } catch {} }
        abortAllDetails();
        setOpenGroups(new Set());
        setEmployeeItems({});
        setGroups([]);
        setPage(1);
    }, [fromDate, toDate, selectedUnit?.id, employeesKey, pageSize]);

    // Fetch SUMMARY (status=null)
    useEffect(() => {
        const ctrl = new AbortController();
        summaryCtrlRef.current = ctrl;

        const loadSummary = async () => {
        setIsLoading(true);
        setIsError(false);
        try {
            const res = await getJustificationRequestsSummary(
            token,
            fromDate,
            toDate,
            'null',
            selectedUnit?.id || undefined,
            selectedEmployees.map(e => e.id),
            page,
            pageSize,
            ctrl.signal, 
            'justifications'
            );

            const results = Array.isArray(res?.results) ? res.results : [];
            const normalized = results.map(r => ({
                key: r?.employee?.id ?? r?.employee?.cf,
                employee: r?.employee,
                total_requests: r?.total_requests ?? 0,
            }));

            setGroups(normalized);
            const count = Number(res?.count ?? normalized.length);
            setTotalPages(Math.max(1, Math.ceil(count / pageSize)));

            // totale pendenti globale (per pulsanti Approva/Rifiuta tutte)
            const all = Number(res?.pending_total ?? res?.totals?.pending_count ?? 0);
            setTotalPendingAll(all);

            // mantieni aperte solo le righe ancora visibili
            setOpenGroups(prev => {
            const visible = new Set(normalized.map(g => g?.employee?.id ?? g.key));
            const next = new Set();
            prev.forEach(k => { if (visible.has(k)) next.add(k); });
            return next;
            });
        } catch (err) {
            if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
            console.error('Summary error:', err);
            setIsError(true);
            }
        } finally {
            if (summaryCtrlRef.current === ctrl) setIsLoading(false);
        }
        };

        loadSummary();
        return () => { try { ctrl.abort(); } catch {} };
    }, [token, fromDate, toDate, selectedUnit?.id, employeesKey, page, pageSize, refreshKey]);

    // Helpers UI mese
    const handlePreviousMonth = () =>
        setSelectedMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const handleNextMonth = () =>
        setSelectedMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

    // Handler passati alla SchedulerContextBar
    const handleSelectUnit = (unit) => {
        setSelectedUnit(unit || null);
        // reset dataset dipendenti/dettagli quando cambio UO
        setSelectedEmployees([]);
        setEmployeeItems({});
        setOpenGroups(new Set());
        setPage(1);
    };
    const handleResetUnit = () => {
        setSelectedUnit(null);
        if (selectedEmployees.length) setSelectedEmployees([]);
        setPage(1);
    };
    const handleEmployeesChange = (list) => {
        setSelectedEmployees(Array.isArray(list) ? list : []);
        setPage(1);
    };
    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setPage(1);
    };

    // Stato righe aperte
    const isGroupOpen = (employeeId) => openGroups.has(employeeId);
    const allOpen = useMemo(
        () => hasGroups && groups.every(g => openGroups.has(g.employee?.id ?? g.key)),
        [hasGroups, groups, openGroups]
    );

    // Fetch dettaglio per dipendente
    const fetchEmployeeRequests = useCallback(async (empId, pageEmp = 1) => {
        abortDetail(empId);
        const ctrl = new AbortController();
        detailCtrlsRef.current.set(empId, ctrl);

        setEmployeeItems(prev => ({
        ...prev,
        [empId]: { ...(prev[empId] || {}), isLoading: true, error: null }
        }));

        try {
            const res = await getJustificationRequestsCompanyList(
                token,
                fromDate,
                pageEmp,
                EMP_PAGE_SIZE,
                'null',
                selectedUnit?.id,
                [empId],
                toDate,
                ctrl.signal,
                'justifications'
            );

            const items = Array.isArray(res?.results) ? res.results : [];
            const count = Number(res?.count ?? items.length);
            const totalPagesEmp = Math.max(1, Math.ceil(count / EMP_PAGE_SIZE));

            // se pagina fuori range dopo operazioni, rimbalza all'ultima valida
            if (items.length === 0 && pageEmp > 1 && pageEmp > totalPagesEmp) {
                const fallback = totalPagesEmp;
                const res2 = await getJustificationRequestsCompanyList(
                token,
                fromDate,
                fallback,
                EMP_PAGE_SIZE,
                'null',
                selectedUnit?.id,
                [empId],
                toDate,
                ctrl.signal,
                'justifications'
                );
                const items2 = Array.isArray(res2?.results) ? res2.results : [];
                const count2 = Number(res2?.count ?? items2.length);
                const total2 = Math.max(1, Math.ceil(count2 / EMP_PAGE_SIZE));
                setEmployeeItems(prev => ({
                ...prev,
                [empId]: { items: items2, isLoading: false, error: null, page: fallback, totalPages: total2, count: count2 }
                }));
                return;
            }

            setEmployeeItems(prev => ({
                ...prev,
                [empId]: { items, isLoading: false, error: null, page: pageEmp, totalPages: totalPagesEmp, count }
            }));
        } catch (e) {
            if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
                setEmployeeItems(prev => ({
                ...prev,
                [empId]: { ...(prev[empId] || {}), isLoading: false }
                }));
            } else {
                console.error('Dettaglio dipendente error:', e);
                setEmployeeItems(prev => ({
                ...prev,
                [empId]: { ...(prev[empId] || {}), isLoading: false, error: e?.message || 'Errore' }
                }));
            }
        } finally {
            const current = detailCtrlsRef.current.get(empId);
            if (current === ctrl) detailCtrlsRef.current.delete(empId);
        }
    }, [token, fromDate, toDate, selectedUnit?.id]);

    // Toggle e apertura/chiusura gruppi
    const toggleGroup = (employeeId) => {
        abortDetail(employeeId);
        setOpenGroups(prev => {
        const n = new Set(prev);
        if (n.has(employeeId)) {
            n.delete(employeeId);
        } else {
            n.add(employeeId);
            if (!employeeItems[employeeId]?.items) fetchEmployeeRequests(employeeId, 1);
        }
        return n;
        });
    };
    const toggleAllGroups = () => {
        if (!hasGroups) return;
        const areAllOpen = groups.every(g => openGroups.has(g.employee?.id ?? g.key));
        if (areAllOpen) {
        abortAllDetails();
        setOpenGroups(new Set());
        return;
        }
        abortAllDetails();
        const next = new Set(openGroups);
        groups.forEach(g => {
        const empId = g.employee?.id ?? g.key;
        next.add(empId);
        if (!employeeItems[empId]?.items) fetchEmployeeRequests(empId, 1);
        });
        setOpenGroups(next);
    };

    // Navigazione per-dipendente
    const gotoEmpPage = (empId, target) => {
        const cur = employeeItems[empId];
        if (!cur) return;
        const total = cur.totalPages || 1;
        const pageTarget = Math.min(total, Math.max(1, target));
        if (pageTarget !== cur.page) {
        abortDetail(empId);
        fetchEmployeeRequests(empId, pageTarget);
        scrollToEmpTop(empId);
        }
    };
    const handleEmpPrev = (empId) => gotoEmpPage(empId, (employeeItems[empId]?.page || 1) - 1);
    const handleEmpNext = (empId) => gotoEmpPage(empId, (employeeItems[empId]?.page || 1) + 1);

    // Azioni su singola richiesta
    const handleStatusChange = async (request, newStatus) => {
        if (!request) return;
        const empId = request?.employee?.id;
        try {
            const currentDate = new Date();
            await updateJustificationRequestStatus(token, request.id, newStatus, currentDate);

            setRefreshKey(k => k + 1);

            const curPage = employeeItems[empId]?.page || 1;
            abortDetail(empId);
            await fetchEmployeeRequests(empId, curPage);

            await Swal.fire({
                title: 'Salvato!',
                text: `Richiesta ${newStatus === 'approved' ? 'approvata' : 'rifiutata'} con successo.`,
                icon: "success",
                timer: 1200,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error updating justification request status:', error);
            const details = error.response?.data ? Object.values(error.response.data).flat() : [];
            const msg = details.length ? details.join('\n') : 'Errore nell\'aggiornamento della richiesta.';
            Swal.fire({ icon: 'error', title: 'Oops...', text: msg });
        }
    };

    // Bulk helpers
    const pickCounts = (data = {}) => {
        const updated = Number(data.updated_count ?? 0);
        const notUpdated = Number(
            data.not_updated_count ??
            ((Number(data.month_blocked_count ?? 0)) +
            (Number(data.not_working_hours_count ?? 0)) +
            (Number(data.failed_other_count ?? 0)))
        );
        return { updated, notUpdated };
    };
    const getEmployeeDisplay = (empId) => {
        const g = groups.find(x => (x.employee?.id ?? x.key) === empId);
        const e = g?.employee;
        return e?.first_name ? `${e.last_name} ${e.first_name}` : (e?.cf || `ID ${empId}`);
    };

    const handleBulkStatusChange = async (empId, newStatus) => {
        const group = groups.find(g => (g.employee?.id ?? g.key) === empId);
        const pendingCount = Number(group?.total_requests ?? 0) || Number(employeeItems[empId]?.count ?? 0);

        if (!pendingCount) {
            await Swal.fire({ icon: 'info', title: 'Nessuna richiesta', text: 'Non ci sono richieste pendenti per questo dipendente nel periodo selezionato.' });
            return;
        }

        const actionVerb = newStatus === 'approved' ? 'approvare' : 'rifiutare';
        const confirmText = newStatus === 'approved' ? 'Sì, approva tutte' : 'Sì, rifiuta tutte';
        const employeeName = getEmployeeDisplay(empId);

        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            title: `Confermi l'operazione?`,
            html: `Vuoi <b>${actionVerb}</b> tutte le <b>${pendingCount}</b> richieste di ${employeeName}?`,
            showCancelButton: true,
            confirmButtonText: confirmText,
            cancelButtonText: 'Annulla',
            reverseButtons: true,
            });
        if (!isConfirmed) return;

        try {
            const res = await bulkUpdateJustificationRequestsStatus(
                token,
                newStatus,
                fromDate,
                toDate,
                selectedUnit?.id ?? null,
                [empId],
                'null',
                null,
                'justifications'
            );

            const { updated, notUpdated } = pickCounts(res);

            if (updated > 0) {
                setGroups(prev =>
                prev
                    .map(g => {
                    const gid = g.employee?.id ?? g.key;
                    return gid === empId
                        ? { ...g, total_requests: Math.max(0, (g.total_requests || 0) - updated) }
                        : g;
                    })
                    .filter(g => (g.total_requests || 0) > 0)
                );
            }

            setRefreshKey(k => k + 1);

            const curPage = employeeItems[empId]?.page || 1;
            abortDetail(empId);
            await fetchEmployeeRequests(empId, curPage);

            await Swal.fire({
                icon: 'success',
                title: 'Operazione completata',
                html: `Aggiornate: <b>${updated}</b><br/>Non aggiornate: <b>${notUpdated}</b>`,
                showConfirmButton: true,
            });
        } catch (error) {
            const data = error?.response?.data || {};
            const { updated, notUpdated } = pickCounts(data);
            const detailMsg = data.detail || 'Operazione non completata';
            await Swal.fire({
                icon: 'error',
                title: detailMsg,
                html: `Aggiornate: <b>${updated}</b><br/>Non aggiornate: <b>${notUpdated}</b>`,
            });
        }
    };
    const handleApproveAll = (empId) => handleBulkStatusChange(empId, 'approved');
    const handleRejectAll  = (empId) => handleBulkStatusChange(empId, 'rejected');

    // Bulk globale (tutti nel mese e, se selezionata, nell'UO)
    const handleProcessAllRequests = async (newStatus) => {
        const n = Number(totalPendingAll || 0);
        if (n <= 0) {
            await Swal.fire({ icon: 'info', title: 'Nessuna richiesta pendente' });
            return;
        }

        const verbo    = newStatus === 'approved' ? 'approvare' : 'rifiutare';
        const conferma = newStatus === 'approved' ? 'Sì, approva tutte' : 'Sì, rifiuta tutte';
        const labelN   = n === 1 ? 'richiesta' : 'richieste';

        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            title: 'Conferma operazione',
            html: `Vuoi ${verbo} tutte le <b>${n}</b> ${labelN} presenti nel mese?`,
            showCancelButton: true,
            confirmButtonText: conferma,
            cancelButtonText: 'Annulla',
            reverseButtons: true
        });

        if (!isConfirmed) return;

        try {
            Swal.fire({ title: 'Elaborazione in corso...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            const res = await bulkUpdateJustificationRequestsStatus(
                token,
                newStatus,
                fromDate,
                toDate,
                selectedUnit?.id ?? null,
                [],
                'null',
                null,
                'justifications'
            );

            Swal.close();

            const { updated, notUpdated } = pickCounts(res);
            
            setTotalPendingAll(prev => {
                const next = Math.max(0, Number(prev || 0) - Number(updated || 0));
                return next;
            });

            abortAllDetails();
            setOpenGroups(new Set());
            setRefreshKey(k => k + 1);

            await Swal.fire({
            icon: 'success',
            title: 'Operazione completata',
            html: `Aggiornate: <b>${updated}</b><br/>Non aggiornate: <b>${notUpdated}</b>`,
            showConfirmButton: true
            });
        } catch (error) {
            Swal.close();
            const data = error?.response?.data || {};
            const { updated, notUpdated } = pickCounts(data);
            const detailMsg = data.detail || 'Operazione non completata';

            await Swal.fire({
            icon: 'error',
            title: detailMsg,
            html: `Aggiornate: <b>${updated}</b><br/>Non aggiornate: <b>${notUpdated}</b>`
            });
        }
   };


    const handleDeleteRequest = async (request) => {
        const empId = request?.employee?.id;
        const { isConfirmed } = await Swal.fire({
            title: 'Conferma Eliminazione',
            html: `
                <div class="mt-2">
                <p class="text-gray-500">
                    Sei sicuro di voler eliminare la richiesta di giustificativo del dipendente 
                    ${request.employee.last_name ? `${request.employee.last_name} ${request.employee.first_name}` : request.employee.cf || ''}?
                </p>
                </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla',
            reverseButtons: true
        });
        if (!isConfirmed) return;

        try {
            Swal.fire({ title: 'Eliminazione in corso...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await deleteJustificationRequest(token, request.id);
            
            setTotalPendingAll?.((prev) => Math.max(0, Number(prev || 0) - 1));

            setGroups(prev => prev
                .map(g => {
                const gid = g.employee?.id ?? g.key;
                return gid === empId ? { ...g, total_requests: Math.max(0, (g.total_requests || 0) - 1) } : g;
                })
                .filter(g => (g.total_requests || 0) > 0)
            );

            const curPage = employeeItems[empId]?.page || 1;
            abortDetail(empId);
            await fetchEmployeeRequests(empId, curPage);

            Swal.close();
            Swal.fire({ title: 'Eliminato!', text: 'Richiesta eliminata.', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            console.error('Error deleting justification request:', error);
            Swal.close();
            Swal.fire({ icon: 'error', title: 'Oops...', text: 'Errore durante l\'eliminazione della richiesta.' });
        }
    };

    // Pair/Unpair OU + Note
    const handleSelectOUtoPair = async (selectedUnitToPair) => {
        if (!selectedJustification || !selectedUnitToPair) return;
        const empId = selectedJustification?.employee?.id;
        try {
        const updated = await updateJustificationRequestStatus(
            token,
            selectedJustification.id,
            undefined, undefined, undefined, undefined, undefined,
            selectedUnitToPair.id
        );
        setEmployeeItems(prev => {
            const cur = prev[empId] || { items: [] };
            const nextItems = (cur.items || []).map(r => r.id === selectedJustification.id ? { ...r, ...updated } : r);
            return { ...prev, [empId]: { ...cur, items: nextItems } };
        });
        Swal.fire({ title: 'Salvato!', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
        console.error('Error updating OU:', error);
        let msg = 'Si è verificato un errore nell\'aggiornamento.';
        if (error.response?.data) {
            const em = Object.values(error.response.data).flat().join(', ');
            if (em) msg = em;
        }
        Swal.fire({ title: 'Errore!', text: msg, icon: 'error', confirmButtonText: 'Chiudi' });
        }
    };

    const handleDeletePairedUnit = async (justificationId, empId) => {
        if (!justificationId) return;
        try {
            await updateJustificationRequestStatus(token, justificationId, undefined, undefined, undefined, undefined, undefined, null);
            setEmployeeItems(prev => {
                const cur = prev[empId] || { items: [] };
                const nextItems = cur.items.map(r => (r.id === justificationId ? { ...r, ou: null, ou_name: null } : r));
                return { ...prev, [empId]: { ...cur, items: nextItems } };
            });
            Swal.fire({ title: 'Salvato!', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            console.error('Errore durante l\'eliminazione OU:', error);
            Swal.fire({ title: 'Errore!', text: 'Si è verificato un errore durante l\'eliminazione dell\'unità associata.', icon: 'error', confirmButtonText: 'Chiudi' });
        }
    };

    const handleSaveNote = async (justificationId, empId) => {
        if (!editingNoteText.trim()) return;
        try {
            Swal.fire({ title: 'Salvataggio in corso...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await updateJustificationRequestStatus(
                token,
                justificationId,
                undefined, undefined, undefined, undefined,
                editingNoteText,
                undefined
            );
            setEmployeeItems(prev => {
                const cur = prev[empId] || { items: [] };
                const nextItems = cur.items.map(r => (r.id === justificationId ? { ...r, note: editingNoteText } : r));
                return { ...prev, [empId]: { ...cur, items: nextItems } };
            });
            setEditingNoteId(null);
            setEditingNoteText('');
            Swal.close();
            Swal.fire({ title: 'Salvato!', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            console.error('Errore aggiornamento note:', error);
            Swal.close();
            let backendMsg = 'Errore durante il salvataggio della nota.';
            if (error?.response?.data) {
                const data = error.response.data;
                if (Array.isArray(data)) backendMsg = data.join(' ');
                else if (typeof data === 'object') {
                const msgs = Object.values(data).flat().filter(Boolean);
                if (msgs.length) backendMsg = msgs.join(' ');
                } else if (typeof data === 'string') backendMsg = data;
            }
            Swal.fire({ title: 'Errore!', text: backendMsg, icon: 'error', confirmButtonText: 'Chiudi' });
        }
    };

    const handleCancelNoteEdit = () => {
        setEditingNoteId(null);
        setEditingNoteText('');
    };


    // Riga dettaglio richiesta
    const JustificationRow = ({ item }) => {
        const { time: endTime } = formatJustificationEnd(item.datetime_from, item.datetime_to);
        const hasOU = Boolean(item?.ou);
        const empId = item?.employee?.id;

        return (
            <TableRow className="border-b border-gray-200">
                <TableCell sx={{ width: '14%' }}>
                    <span className="font-semibold">{moment(item.datetime_from).format('DD/MM/YYYY')}</span>
                </TableCell>

                <TableCell sx={{ width: '52%' }}>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm px-3 py-2 bg-white">
                        <div className="text-indigo-700 font-semibold uppercase tracking-wide">
                            {item.justification?.justification?.description}
                        </div>

                        {moment(item.datetime_from).format('HH:mm:ss') === '00:00:00' &&
                        moment(item.datetime_to).format('HH:mm:ss') === '23:59:59' ? (
                            <div className="text-gray-600 text-sm">
                                dal <span className="font-semibold">{moment(item.datetime_from).format('DD/MM/YYYY')}</span>{' '}
                                al <span className="font-semibold">{moment(item.datetime_to).format('DD/MM/YYYY')}</span>
                            </div>
                        ) : (
                            <div className="text-gray-600 text-sm">
                                Dalle <span className="font-semibold">{moment(item.datetime_from).format('HH:mm')}</span>{' '}
                                alle <span className="font-semibold">{endTime}</span>
                            </div>
                        )}

                        {/* NOTE */}
                        <div className="mt-2 text-sm text-gray-700">
                            <div className="grid grid-cols-[auto,1fr,auto] items-start gap-2">
                                <span className="font-semibold text-indigo-700 pt-0.5">Note:</span>

                                {editingNoteId === item.id ? (
                                    <div className="flex items-start gap-2 w-full">
                                        <input
                                            type="text"
                                            className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                            value={editingNoteText}
                                            onChange={(e) => setEditingNoteText(e.target.value)}
                                            autoFocus
                                        />
                                        <div className="flex items-center gap-1 shrink-0">
                                            <CustomTooltipComponent title="Salva nota" placement="top" arrow>
                                                <button
                                                    className="bg-indigo-500 hover:bg-indigo-600 px-1.5 py-1 rounded-md shadow text-white transition"
                                                    onClick={() => handleSaveNote(item.id, empId)}
                                                >
                                                    <SaveOutlinedIcon sx={{ width: 18, height: 18 }} />
                                                </button>
                                            </CustomTooltipComponent>
                                            <CustomTooltipComponent title="Annulla" placement="top" arrow>
                                                <button
                                                    className="bg-gray-300 hover:bg-gray-400 px-1.5 py-1 rounded-md shadow text-white transition"
                                                    onClick={handleCancelNoteEdit}
                                                >
                                                    <RestartAltOutlinedIcon sx={{ width: 18, height: 18 }} />
                                                </button>
                                            </CustomTooltipComponent>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-start justify-between gap-2 w-full">
                                            {item.note?.trim() ? (
                                                <span className="text-gray-800 leading-5 whitespace-pre-line break-words">
                                                    {item.note}
                                                </span>
                                            ) : (
                                                <button
                                                    className="text-gray-400 hover:text-indigo-700 italic text-left p-0"
                                                    onClick={() => { setEditingNoteId(item.id); setEditingNoteText(''); }}
                                                >
                                                    Non presenti. Clicca per aggiungere
                                                </button>
                                            )}

                                            {item.note?.trim() && (
                                                <div className="shrink-0">
                                                    <CustomTooltipComponent title="Modifica nota" placement="top" arrow>
                                                        <button
                                                            className="p-1 rounded-full bg-white border border-gray-300 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition"
                                                            onClick={() => { setEditingNoteId(item.id); setEditingNoteText(item.note || ''); }}
                                                        >
                                                            <PencilSquareIcon className="h-4 w-4" />
                                                        </button>
                                                    </CustomTooltipComponent>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {hasOU ? (
                            <div className="mt-2 -mx-3 -mb-2 px-3 py-2 rounded-md bg-indigo-50 text-sm text-gray-700 flex items-center gap-2">
                                <span className="font-semibold">Unità associata:</span> {item.ou_name}
                                    <CustomTooltipComponent title="Rimuovi associazione" placement="top" arrow>
                                        <button
                                            className="p-1 rounded-full hover:bg-red-50 text-gray-600 hover:text-red-600 transition"
                                            onClick={() => handleDeletePairedUnit(item.id, empId)}
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </CustomTooltipComponent>
                            </div>
                        ) : (
                            <div className="mt-2">
                                <button
                                    className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ring-1 ring-inset ring-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition"
                                    onClick={() => { setSelectedJustification(item); setShowUnitSelection(true); }}
                                >
                                    Associa Unità
                                </button>
                            </div>
                        )}
                    </div>
                </TableCell>

                <TableCell align="center" sx={{ width: '34%' }}>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        <CustomTooltipComponent title="Approva richiesta" placement="top" arrow>
                            <button
                                onClick={() => handleStatusChange(item, 'approved')}
                                className="p-2 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800 shadow-sm transition"
                            >
                                <CheckIcon className="h-5 w-5" />
                            </button>
                        </CustomTooltipComponent>

                        <CustomTooltipComponent title="Rifiuta richiesta" placement="top" arrow>
                            <button
                                onClick={() => handleStatusChange(item, 'rejected')}
                                className="p-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 shadow-sm transition"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </CustomTooltipComponent>

                        <CustomTooltipComponent title="Elimina richiesta" placement="top" arrow>
                            <button
                                onClick={() => handleDeleteRequest(item)}
                                className="p-2 rounded-full bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300 shadow-sm transition"
                            >
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        </CustomTooltipComponent>
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    // Riga gruppo (dipendente)
    const GroupRow = ({ group }) => {
        const fullName = group.employee?.first_name
        ? `${group.employee.last_name} ${group.employee.first_name}`
        : group.employee?.cf;
        const empId = group.employee?.id ?? group.key;
        const open = isGroupOpen(empId);

        const detail = employeeItems[empId];
        const isDetailLoading = detail?.isLoading;
        const detailItems = detail?.items || [];
        const empPage = detail?.page || 1;
        const empTotalPages = detail?.totalPages || 1;

        return (
        <>
            <TableRow
                onClick={() => toggleGroup(empId)}
                className="hover:bg-gray-50 cursor-pointer transition"
                aria-expanded={open}
            >
                <TableCell sx={{ width: 56 }}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); toggleGroup(empId); }}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{fullName}</TableCell>
                <TableCell align="center">{group.total_requests ?? 0}</TableCell>
                <TableCell align="right">
                    <div className="flex items-center justify-end gap-2">
                        <CustomTooltipComponent title="Approva tutte" placement="top" arrow>
                            <button
                                onClick={(e) => { e.stopPropagation(); handleApproveAll(empId); }}
                                className="p-2 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800 shadow-sm transition"
                                aria-label="Approva tutte"
                            >
                                <CheckIcon className="h-5 w-5" />
                            </button>
                        </CustomTooltipComponent>

                        <CustomTooltipComponent title="Rifiuta tutte" placement="top" arrow>
                            <button
                                onClick={(e) => { e.stopPropagation(); handleRejectAll(empId); }}
                                className="p-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 shadow-sm transition"
                                aria-label="Rifiuta tutte"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </CustomTooltipComponent>
                    </div>
                </TableCell>
                <TableCell />
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5} className="bg-indigo-50">
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <div ref={setEmpAnchorRef(empId)} />
                            <Typography variant="p" gutterBottom component="div" className='font-semibold text-indigo-800 uppercase'>
                                Richieste di {fullName}
                            </Typography>

                            {isDetailLoading ? (
                                <div className="py-4"><Fetching /></div>
                            ) : (
                                <>
                                    <Table size="small" aria-label="richieste" className="rounded-lg">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: '14%' }}>Data</TableCell>
                                                <TableCell style={{ width: '52%' }}>Dettagli</TableCell>
                                                <TableCell style={{ width: '34%' }} align="center">Azioni</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody className="bg-gray-50 shadow-lg">
                                            {detailItems.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center" className="py-6 italic text-sm text-gray-500">
                                                        Nessuna richiesta da elaborare presente per questo periodo
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                detailItems.map(item => (
                                                    <JustificationRow key={item.id} item={item} />
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>

                                    {detail?.count > EMP_PAGE_SIZE && (
                                        <div className="flex justify-between mt-4">
                                            <button
                                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                                onClick={() => handleEmpPrev(empId)}
                                                disabled={empPage === 1}
                                            >
                                                Precedente
                                            </button>
                                            <span className='text-sm font-medium text-gray-900'>Pagina {empPage} di {empTotalPages}</span>
                                            <button
                                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                                onClick={() => handleEmpNext(empId)}
                                                disabled={empPage === empTotalPages}
                                            >
                                                Successivo
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
        );
    };

    // Render
    return (
        <div className="min-h-full">
            <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">
                <div>
                    <SchedulerContextBar
                        className="w-full"
                        units={units}
                        selectedUnit={selectedUnit}
                        onSelectUnit={handleSelectUnit}
                        onResetUnit={handleResetUnit}
                        isUnitsLoading={isUnitsLoading}
                        token={token}
                        selectedEmployees={selectedEmployees}
                        onEmployeesChange={handleEmployeesChange}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                        contractStartDate={fromDate}
                        contractEndDate={toDate}
                    />
                </div>
                {/* Toggle all */}
                <div>
                <div className="px-3 py-3 w-full bg-white" style={{ position: 'relative', zIndex: 11 }}>
                    <div className="flex items-center justify-end">
                        {hasGroups ? (
                            <button
                                onClick={toggleAllGroups}
                                className="inline-flex items-center gap-2 text-sm px-4 py-1 rounded-md font-semibold whitespace-nowrap
                                        bg-indigo-600 text-white hover:bg-indigo-500 transition"
                            >
                                {allOpen ? 'Chiudi tutte' : 'Apri tutte le righe'}
                                {allOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            </button>
                        ) : (
                            <div className='h-8'></div>
                        )}
                    </div>
                </div>

                {/* Selettore mese */}
                <div className="grid grid-cols-1 w-full pt-4 border border-gray-200 rounded-md">
                    <div
                        style={{ flex: '1 1 auto', width: 'auto' }}
                        className="pb-5 pt-2 px-4 border-l border-r border-gray-200 text-center w-24 text-xs font-semibold text-gray-600 uppercase align-middle"
                    >
                        <div className="grid md:grid-cols-5 grid-cols-3">
                            <div className="col-span-1 p-2 flex justify-end me-3">
                                <button className="text-gray-700 font-semibold px-1 hover:bg-gray-100" onClick={handlePreviousMonth}>
                                    <ChevronLeftIcon className="w-5 h-5 cursor-pointer" />
                                </button>
                            </div>
                            <div className="col-span-1 p-2 flex md:justify-end me-3">Seleziona Mese</div>
                            <div className="col-span-1 p-2 justify-start md:hidden block">
                                <button className="text-gray-700 font-semibold px-1 hover:bg-gray-100" onClick={handleNextMonth}>
                                    <ChevronRightIcon className="w-5 h-5 cursor-pointer" />
                                </button>
                            </div>
                            <div
                                onClick={() => setCalendarVisible(v => !v)}
                                className="col-span-3 md:col-span-2 border border-gray-300 rounded-md shadow-md p-2 pl-4 flex items-center md:w-4/5 w-full justify-between cursor-pointer"
                            >
                                <div>
                                    {selectedMonth.toLocaleDateString('it-IT', { month: 'long' })}, {selectedMonth.getFullYear()}
                                </div>
                                {calendarVisible
                                    ? <ChevronUpIcon className="h-4 w-4 bg-indigo-100 rounded-md" />
                                    : <ChevronDownIcon className="h-4 w-4 bg-indigo-100 rounded-md" />}
                            </div>
                            <div className="col-span-1 p-2 hidden md:block">
                                <button className="text-gray-700 font-semibold px-1 hover:bg-gray-100" onClick={handleNextMonth}>
                                    <ChevronRightIcon className="w-5 h-5 cursor-pointer" />
                                </button>
                            </div>
                        </div>

                        {calendarVisible && (
                            <CalendarMonthSelect
                                selectedMonth={selectedMonth}
                                onMonthSelect={setSelectedMonth}
                                onCloseCalendar={() => setCalendarVisible(false)}
                            />
                        )}
                    </div>
                </div>

                {/* contenuti */}
                {isError && 'Error loading data...'}
                {isLoading && <Fetching />}

                {!isLoading && !isError && (
                <TableContainer component={Paper} className="border border-gray-300 mt-3">
                    <Table aria-label="collapsible table">
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>Dipendente</TableCell>
                                <TableCell align="center">N. Richieste</TableCell>

                                    {totalPendingAll > 0 ? (
                                        <TableCell align="right" colSpan={2} sx={{ p: 0 }}>
                                            <div className="flex justify-end items-center gap-2 pr-2">
                                            <button
                                                onClick={() => handleProcessAllRequests('approved')}
                                                className="text-sm px-4 py-1 rounded-md font-semibold whitespace-nowrap bg-indigo-600 text-white hover:bg-indigo-500"
                                            >
                                                Approva tutte ({totalPendingAll})
                                            </button>
                                            <button
                                                onClick={() => handleProcessAllRequests('rejected')}
                                                className="text-sm font-semibold text-gray-600 bg-gray-200 px-4 py-1 rounded hover:bg-gray-300"
                                            >
                                                Rifiuta tutte ({totalPendingAll})
                                            </button>
                                            </div>
                                        </TableCell>
                                    ) : (
                                        <TableCell colSpan={2} />
                                    )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {groups.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" className="py-10 italic text-sm text-gray-500">
                                    Nessuna richiesta da elaborare presente per questo periodo
                                    </TableCell>
                                </TableRow>
                            ) : (
                                groups.map((group) => (
                                    <GroupRow key={group.employee?.id ?? group.key} group={group} />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                )}

                {totalPages > 1 && (
                    <div className="flex justify-between mt-8">
                        <button
                            className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            Precedente
                        </button>
                        <span className='text-sm font-medium text-gray-900'>Pagina {page} di {totalPages}</span>
                        <button
                            className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        >
                            Successivo
                        </button>
                    </div>
                )}
            </div>

            {showUnitSelection && (
                <OuSelectionPair
                    onClose={() => setShowUnitSelection(false)}
                    justification={selectedJustification}
                    onSelectOUtoPair={handleSelectOUtoPair}
                />
            )}
            </div>
        </div>
    );
};

export default GiustificativiRichiesteNull;